import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

function CompanyBillingSummaryModal({ open, loading, data, onClose }) {
  const detailData = data
  const toNum = (v) => {
    const n = parseFloat(v || 0)
    return Number.isFinite(n) ? n : 0
  }
  const orderTotals = (detailData?.pricing_services || []).reduce((acc, svc) => {
    const itemCount = toNum(svc.item_count || 1)
    const amount = toNum(svc.service_amount)
    const gstPercent = toNum(svc.service_gst_percentage)
    const orderTotal =
      svc.order_total_amount !== undefined && svc.order_total_amount !== null
        ? toNum(svc.order_total_amount)
        : (amount * itemCount) + ((amount * itemCount * gstPercent) / 100)
    acc.serviceAmount += amount
    acc.count += itemCount
    acc.orderTotal += orderTotal
    return acc
  }, { serviceAmount: 0, count: 0, orderTotal: 0 })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Company Billing Summary</DialogTitle>
      <DialogContent>
        {loading || !detailData ? (
          <Typography variant="body2" color="textSecondary">Loading...</Typography>
        ) : (
          <>
            <Box mb={2} p={2} bgcolor="#f0f4ff" borderRadius={1} border={1} borderColor="grey.300">
              <Typography variant="subtitle2" fontWeight="bold">{detailData.company_name || 'Company'}</Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Typography variant="caption" color="textSecondary">Student Strength: {detailData.student_strength ?? '-'}</Typography>
              </Box>
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Orders</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">Service Amount</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">GST %</TableCell>
                  <TableCell align="right">Order Total</TableCell>
                  <TableCell align="right">Issued Invoiced</TableCell>
                  <TableCell align="right">Invoiced</TableCell>
                  <TableCell align="right">Invoice Discount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detailData.pricing_services || []).map((svc, idx) => (
                  <TableRow key={`ord-${idx}`}>
                    <TableCell>{svc.service_name ?? '-'}</TableCell>
                    <TableCell>{svc.plan_name ?? '-'}</TableCell>
                    <TableCell align="right">{svc.service_amount ?? '-'}</TableCell>
                    <TableCell align="right">{svc.item_count ?? '-'}</TableCell>
                    <TableCell align="right">{svc.service_gst_percentage ?? '-'}</TableCell>
                    <TableCell align="right">{svc.order_total_amount ?? '-'}</TableCell>
                    <TableCell align="right">{svc.issue_invoice_amount ?? 0}</TableCell>
                    <TableCell align="right">{svc.invoice_amount ?? 0}</TableCell>
                    <TableCell align="right">{svc.invoice_discount_amount ?? '-'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f7fb' }}>
                  <TableCell colSpan={2}><strong>Total (All Services)</strong></TableCell>
                  <TableCell align="right"><strong>{orderTotals.serviceAmount.toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>{orderTotals.count.toFixed(2)}</strong></TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right"><strong>{orderTotals.orderTotal.toFixed(2)}</strong></TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box mt={3} />

            {/* Issue invoice section summary */}
            {(() => {
              const iiRows = Array.isArray(detailData.pricing_services)
                ? detailData.pricing_services.flatMap(s => (Array.isArray(s.issue_invoice_items) ? s.issue_invoice_items : []))
                : []
              const iiCount = new Set(iiRows.map(r => r.issue_invoice).filter(Boolean)).size
              const iiTotal = iiRows.reduce((acc, r) => acc + (parseFloat(r.total || 0) || 0), 0)
              const iiDiscount = iiRows.reduce((acc, r) => acc + (parseFloat(r.discount_amount || 0) || 0), 0)
              return (
                <Accordion defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">Issue Invoice Summary (Service-wise)</Typography>
                      <Box display="flex" gap={3} flexWrap="wrap">
                        <Typography variant="caption" color="textSecondary">Issued Invoices: {iiCount}</Typography>
                        <Typography variant="caption" color="textSecondary">Total Issued: {iiTotal.toFixed(2)}</Typography>
                        <Typography variant="caption" color="textSecondary">Discount: {iiDiscount.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Issue Invoice Details (Service-wise)</Typography>
                    {(detailData.pricing_services || []).map((svc, idx) => (
                      <Box key={`ii-sec-${idx}`} mb={2}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                          {svc.service_name || 'Service'}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Issue Invoice #</TableCell>
                              <TableCell>Issue Date</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Count</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Tax %</TableCell>
                              <TableCell align="right">Tax</TableCell>
                              <TableCell align="right">Total Excl Tax</TableCell>
                              <TableCell align="right">Discount</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(svc.issue_invoice_items || []).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11}>-</TableCell>
                              </TableRow>
                            ) : (
                              (svc.issue_invoice_items || []).map((it, j) => (
                                <TableRow key={`ii-${idx}-${j}`}>
                                  <TableCell>{it.invoice_number || it.issue_invoice || '-'}</TableCell>
                                  <TableCell>{it.issue_date || '-'}</TableCell>
                                  <TableCell>{it.status || '-'}</TableCell>
                                  <TableCell align="right">{it.item_count ?? '-'}</TableCell>
                                  <TableCell align="right">{it.amount ?? '-'}</TableCell>
                                  <TableCell align="right">{it.tax_percent ?? '-'}</TableCell>
                                  <TableCell align="right">{it.tax_amount ?? '-'}</TableCell>
                                  <TableCell align="right">{it.total_without_tax ?? '-'}</TableCell>
                                  <TableCell align="right">{it.discount_amount ?? '-'}</TableCell>
                                  <TableCell align="right">{it.total ?? '-'}</TableCell>
                                  <TableCell>{it.notes ?? ''}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )
            })()}


            <Box mt={3} />
            {/* Invoice section: summary visible by default, details collapsible */}
            {(() => {
              const invRows = Array.isArray(detailData.pricing_services)
                ? detailData.pricing_services.flatMap(s => (Array.isArray(s.invoice_items) ? s.invoice_items : []))
                : []
              const invCount = new Set(invRows.map(r => r.invoice).filter(Boolean)).size
              const invTotal = invRows.reduce((acc, r) => acc + (parseFloat(r.total || 0) || 0), 0)
              const invDiscount = invRows.reduce((acc, r) => acc + (parseFloat(r.discount_amount || 0) || 0), 0)

              return (
                <Accordion defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">Invoice Summary (Service-wise)</Typography>
                      <Box display="flex" gap={3} flexWrap="wrap">
                        <Typography variant="caption" color="textSecondary">Invoices: {invCount}</Typography>
                        <Typography variant="caption" color="textSecondary">Total Invoiced: {invTotal.toFixed(2)}</Typography>
                        <Typography variant="caption" color="textSecondary">Discount: {invDiscount.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Invoice Details (Service-wise)</Typography>
                    {(detailData.pricing_services || []).map((svc, idx) => (
                      <Box key={`inv-sec-${idx}`} mb={2}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                          {svc.service_name || 'Service'}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Invoice #</TableCell>
                              <TableCell>Issue Date</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Count</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Tax %</TableCell>
                              <TableCell align="right">Tax</TableCell>
                              <TableCell align="right">Total Excl Tax</TableCell>
                              <TableCell align="right">Discount</TableCell>
                              <TableCell align="right">Total</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(svc.invoice_items || []).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={11}>-</TableCell>
                              </TableRow>
                            ) : (
                              (svc.invoice_items || []).map((it, j) => (
                                <TableRow key={`inv-${idx}-${j}`}>
                                  <TableCell>{it.invoice_number || it.invoice || '-'}</TableCell>
                                  <TableCell>{it.issue_date || '-'}</TableCell>
                                  <TableCell>{it.status || '-'}</TableCell>
                                  <TableCell align="right">{it.item_count ?? '-'}</TableCell>
                                  <TableCell align="right">{it.amount ?? '-'}</TableCell>
                                  <TableCell align="right">{it.tax_percent ?? '-'}</TableCell>
                                  <TableCell align="right">{it.tax_amount ?? '-'}</TableCell>
                                  <TableCell align="right">{it.total_without_tax ?? '-'}</TableCell>
                                  <TableCell align="right">{it.discount_amount ?? '-'}</TableCell>
                                  <TableCell align="right">{it.total ?? '-'}</TableCell>
                                  <TableCell>{it.notes ?? ''}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )
            })()}

            <Box mt={3} />
            
            {/* Payments section: summary visible by default, details collapsible */}
            <Accordion defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">Payments Summary</Typography>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Typography variant="caption" color="textSecondary">Payments: {detailData.payments_summary?.payment_count ?? 0}</Typography>
                    <Typography variant="caption" color="textSecondary">Mapped Rows: {detailData.payments_summary?.mapped_row_count ?? 0}</Typography>
                    <Typography variant="caption" color="textSecondary">Total Paid: {detailData.payments_summary?.total_paid_amount ?? '0.00'}</Typography>
                    <Typography variant="caption" color="textSecondary">Latest Date: {detailData.payments_summary?.latest_payment_date ?? '-'}</Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Payments</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Collected By</TableCell>
                      <TableCell>Invoice #</TableCell>
                      <TableCell align="right">Amount Paid</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailData.payments || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9}>-</TableCell>
                      </TableRow>
                    ) : (
                      (detailData.payments || []).map((p, idx) => (
                        <TableRow key={`pay-${idx}`}>
                          <TableCell>{p.payment ?? '-'}</TableCell>
                          <TableCell>{p.payment_date ?? '-'}</TableCell>
                          <TableCell>{p.method ?? '-'}</TableCell>
                          <TableCell>{p.reference ?? ''}</TableCell>
                          <TableCell>{p.status ?? '-'}</TableCell>
                          <TableCell>{p.collected_by ?? '-'}</TableCell>
                          <TableCell>{p.invoice_number ?? p.invoice ?? '-'}</TableCell>
                          <TableCell align="right">{p.amount_paid ?? '-'}</TableCell>
                          <TableCell>{p.notes ?? ''}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>

            <Box mt={3} />
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Invoices (Header) & Paid Summary</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Issued Invoice #</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detailData.invoices || []).map((inv) => (
                  <TableRow key={`inv-${inv.id}`}>
                    <TableCell>{inv.invoice_number}</TableCell>
                    <TableCell>{inv.status}</TableCell>
                    <TableCell align="right">{inv.total_amount}</TableCell>
                    <TableCell align="right">{inv.total_paid}</TableCell>
                    <TableCell align="right">{inv.balance}</TableCell>
                    <TableCell>{inv.issue_invoice_number || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompanyBillingSummaryModal


